import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ghibliArt } from '../../../firebaseInit';
import { uploadGenerateImageToStorage } from '../../../firestore/storageHandler';
import { v4 as uuidv4 } from 'uuid';

// Initial state for the GhibliArt component
const initialState = {
  selectedImage: null,
  base64Image: null,
  processedImage: null,
  loading: false,
  error: null,
  showBeforeAfter: true,
  result: null,
  savedImages: [],
};

// Function to convert image URL to base64
const imageUrlToBase64 = async (url) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (error) {
    console.error('Failed to convert image to base64:', error);
    return null;
  }
};

// Async thunk for generating the image
export const generateGhibliArt = createAsyncThunk(
  'ghibliArt/generate',
  async ({ base64Image, userEmail, prompt, activeFolder }, { rejectWithValue, dispatch }) => {
    try {
      const result = await ghibliArt({ base64Image });
      
      if (result.data.success) {
        // Generate a unique ID for the image
        const imgUid = uuidv4();
        
        // If successful, process and save the image
        if (result.data && result.data.data && result.data.data.image) {
          const imageData = result.data.data.image;
          
          // Convert the image URL to base64 for storage
          const imageString = await imageUrlToBase64(imageData.url);
          
          if (imageString) {
            // Save the image to Firebase storage
            await uploadGenerateImageToStorage({
              imageString,
              prompt: prompt || "Ghibli Art Style Image",
              email: userEmail,
              id: imgUid,
              folderName: activeFolder || "GhibliArt",
              seed: 123, // Default value, adjust as needed
              cfgScale: 7.5, // Default value, adjust as needed
              height: imageData.height,
              width: imageData.width,
              steps: 30, // Default value, adjust as needed
            });
            
            // Add additional data to the result for the reducer to use
            return {
              ...result.data,
              imgUid,
              imageString,
              prompt,
              activeFolder
            };
          }
        }
        
        return result.data;
      } else {
        // Extract specific error message if available
        const errorMessage = result.data.error || result.result?.error || 'Failed to process image';
        return rejectWithValue(errorMessage);
      }
    } catch (error) {
      console.error("Error generating Ghibli art:", error);
      // Check if the error response contains a specific message
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.error) {
          return rejectWithValue(errorData.error);
        } else if (errorData.result && errorData.result.error) {
          return rejectWithValue(errorData.result.error);
        }
      }
      return rejectWithValue('Failed to process image. Please try again.');
    }
  }
);

const ghibliArtSlice = createSlice({
  name: 'ghibliArt',
  initialState,
  reducers: {
    uploadImage: (state, action) => {
      const { imageUrl, base64String } = action.payload;
      state.selectedImage = imageUrl;
      state.base64Image = base64String;
      state.processedImage = null;
      state.error = null;
    },
    removeImage: (state) => {
      state.selectedImage = null;
      state.base64Image = null;
      state.processedImage = null;
      state.error = null;
    },
    setShowBeforeAfter: (state, action) => {
      state.showBeforeAfter = action.payload;
    },
    resetGhibliArt: () => initialState,
    setResult: (state, action) => {
      state.result = action.payload;
    },
    generateAnother: (state) => {
      state.result = null;
      state.showBeforeAfter = true;
    },
    addToSavedImages: (state, action) => {
      state.savedImages.push(action.payload);
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(generateGhibliArt.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(generateGhibliArt.fulfilled, (state, action) => {
        state.loading = false;
        
        // If we have successful image generation with all the data we need
        if (action.payload.imgUid && action.payload.imageString) {
          // Save to result for display
          state.result = {
            result: {
              success: true,
              data: action.payload.data
            }
          };
          
          // Also add to saved images for later access
          state.savedImages.push({
            id: action.payload.imgUid,
            src: action.payload.imageString,
            prompt: action.payload.prompt || "Ghibli Art Style Image",
            folderName: action.payload.activeFolder || "GhibliArt",
            // Include any other data needed for saved images
          });
        } else {
          // Just set the result without saving to savedImages
          state.result = {
            result: {
              success: true,
              data: action.payload.data
            }
          };
        }
        
        state.showBeforeAfter = false;
      })
      .addCase(generateGhibliArt.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  }
});

// Export actions
export const {
  uploadImage,
  removeImage,
  setShowBeforeAfter,
  resetGhibliArt,
  setResult,
  generateAnother,
  addToSavedImages
} = ghibliArtSlice.actions;

// Export selectors
export const selectGhibliArtState = (state) => state.ghibliArt;
export const selectSavedImages = (state) => state.ghibliArt.savedImages;

export default ghibliArtSlice.reducer; 